import { CircularProgress, Box } from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useInterfaceLanguage } from "../hooks/useInterfaceLanguage";
import { sharedApi, getAuthToken } from "../services/sharedApi";
import { useAppDispatch, useAppSelector } from "../store";
import {
  selectToken,
  selectUser,
  selectAvatarUrl,
} from "../store/selectors/authSelectors";
import { selectProfileData } from "../store/selectors/profileSelectors";
import { getCurrentUser, loadAvatarUrl } from "../store/slices/authSlice";
import { fetchSubscriptionSnapshot } from "../store/slices/subscriptionSlice";
import { clearCache } from "../utils/cache/cache";
import { consentManager } from "../utils/consentManager";
import { checkAndCleanObsoleteData } from "../utils/dataVersion";
import { logger } from "../utils/logger";

const MAX_INIT_RETRIES = 5;
const RETRY_DELAY_MS = 2000;

interface AppInitializerProps {
  children: React.ReactNode;
}

/**
 * Компонент для инициализации приложения
 * Загружает данные пользователя и инициализирует профиль
 */
const AppInitializer: React.FC<AppInitializerProps> = ({
  children,
}): React.ReactElement => {
  const { t: translate } = useTranslation();

  const dispatch = useAppDispatch();
  const token = useAppSelector(selectToken);
  const user = useAppSelector(selectUser);
  const profileData = useAppSelector(selectProfileData);
  const avatarUrl = useAppSelector(selectAvatarUrl);
  const [initializing, setInitializing] = useState(() => {
    const token = localStorage.getItem("token");
    const hasAnonymousSession =
      localStorage.getItem("hasAnonymousSession") === "true";
    // Only block rendering if we have a real session to restore
    return !!token && !hasAnonymousSession;
  });
  const [initTimeout, setInitTimeout] = useState(false);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Таймаут для предотвращения бесконечной загрузки
  useEffect(() => {
    const timer = setTimeout(() => {
      if (initializing) {
        logger.warn(
          "[app-init] Initialization timeout reached, completing initialization",
        );
        setInitTimeout(true);
      }
    }, 10000); // 10 секунд таймаут

    return () => clearTimeout(timer);
  }, [initializing]);

  // Используем хук для управления языком интерфейса
  useInterfaceLanguage();

  // Загружаем аватар после успешной загрузки пользователя
  useEffect(() => {
    if (user && user.email && !avatarUrl) {
      logger.debug("[app-init] Loading avatar for user", user.email);
      dispatch(
        loadAvatarUrl({
          email: user.email,
          customAvatarUrl: null, // У нас нет кастомного аватара в базе
          size: 40,
        }),
      );
    }
  }, [dispatch, user, avatarUrl]);

  // Очищаем in-memory кэш один раз при монтировании (независимо от auth)
  useEffect(() => {
    // Проверяем версию данных и очищаем устаревшие данные при необходимости
    checkAndCleanObsoleteData();
    clearCache();
    // Remove the obsolete invitation redirect left by earlier client versions.
    localStorage.removeItem("teacherInvitationReturn");
  }, []);

  useEffect(() => {
    retryCountRef.current = 0;
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, [token]);

  useEffect(() => {
    if (!initializing) {
      return;
    }

    let cancelled = false;

    const clearRetryTimer = (): void => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };

    const initializeApp = async (): Promise<void> => {
      try {
        if (!token) {
          logger.info("[app-init] No token, initialization complete");
          retryCountRef.current = 0;
          clearRetryTimer();
          setInitializing(false);
          return;
        }

        // Проверяем, есть ли анонимная сессия
        const hasAnonymousSession =
          localStorage.getItem("hasAnonymousSession") === "true";
        if (hasAnonymousSession) {
          logger.info(
            "[app-init] Anonymous session detected, skipping user data loading",
          );
          retryCountRef.current = 0;
          clearRetryTimer();
          setInitializing(false);
          return;
        }

        // CRITICAL: Проверяем смену пользователя
        // Если есть токен но нет пользователя, или пользователь не совпадает - загружаем заново
        const shouldLoadUser = !user || !profileData.targetLanguage;

        if (shouldLoadUser) {
          logger.debug("[app-init] Loading user data (new login or reload)");
          try {
            await dispatch(getCurrentUser()).unwrap();
            if (cancelled) return;
            // Синхронизируем согласие cookies с сервером
            try {
              const token = getAuthToken();
              if (token) {
                const serverConsent =
                  await sharedApi.privacy.getLatestConsent(token);
                if (serverConsent) {
                  consentManager.mergeFromServer(serverConsent);
                } else {
                  // Если на сервере нет записи, но локально согласие уже есть — отправим отчёт (некритично при ошибке)
                  await consentManager.syncToServerIfAuthenticated();
                }
              }
            } catch (e) {
              logger.warn("[app-init] Consent sync failed (non-critical)", e);
            }

            // Параллельно предзагружаем подписочную информацию (не блокируем UI)
            if (cancelled) return;
            dispatch(fetchSubscriptionSnapshot(undefined));
          } catch (error: unknown) {
            if (cancelled) return;
            logger.error("[app-init] Failed to load user data", error);
            // При ошибке загрузки пользователя завершаем инициализацию
            setInitializing(false);
            return;
          }
        } else {
          logger.debug("[app-init] User and profile already loaded");
          // Если данные уже есть - просто проверяем актуальность подписки
          if (user && token) {
            if (cancelled) return;
            dispatch(fetchSubscriptionSnapshot(undefined));
          }
        }

        // Завершаем инициализацию
        logger.info("[app-init] Initialization complete");
        retryCountRef.current = 0;
        clearRetryTimer();
        setInitializing(false);
      } catch (error) {
        logger.error(
          "[app-init] Unexpected error during initialization",
          error,
        );
        if (cancelled) {
          return;
        }

        const status =
          typeof error === "object" &&
          error !== null &&
          "status" in error &&
          typeof (error as { status?: unknown }).status === "number"
            ? (error as { status: number }).status
            : undefined;

        if (status === 401 || status === 403) {
          logger.warn(
            "[app-init] Authorization error during initialization, stopping attempts",
            error,
          );
          setInitializing(false);
          return;
        }

        const nextAttempt = retryCountRef.current + 1;
        retryCountRef.current = nextAttempt;

        if (nextAttempt <= MAX_INIT_RETRIES) {
          logger.warn(
            `[app-init] Initialization attempt ${nextAttempt} failed, retrying in ${RETRY_DELAY_MS}ms`,
            error,
          );
          clearRetryTimer();
          retryTimeoutRef.current = setTimeout(() => {
            if (!cancelled) {
              initializeApp().catch((err) =>
                logger.error(
                  "[app-init] Retry attempt failed with unexpected error",
                  err,
                ),
              );
            }
          }, RETRY_DELAY_MS);
        } else {
          logger.error(
            "[app-init] Initialization failed after maximum retry attempts",
            error,
          );
          clearRetryTimer();
          setInitializing(false);
        }
      }
    };

    initializeApp().catch((err) =>
      logger.error("[app-init] Initialization failed", err),
    );

    return () => {
      cancelled = true;
      clearRetryTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- controlled retry loop that intentionally ignores derived values
  }, [dispatch, token, initializing]);

  // Показываем загрузку во время инициализации
  if (initializing) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          gap: 2,
        }}
      >
        <CircularProgress />
        {initTimeout && (
          <Box sx={{ textAlign: "center", color: "text.secondary" }}>
            <div>
              {translate(
                "common:interface.initializationIsTakingLongerThanExpected",
              )}
            </div>
            <div style={{ fontSize: "0.8em", marginTop: 8 }}>
              {translate("common:interface.ifThisPersistsTryRefreshingThePage")}
            </div>
          </Box>
        )}
      </Box>
    );
  }

  return <>{children}</>;
};

export default AppInitializer;
